import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Search, Loader2, RotateCcw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Permission Matrix — Manager-only admin view.
 *
 * Rows = permissions grouped by module.
 * Cols = RBAC roles.
 * Each cell is a checkbox showing the current role↔permission assignment.
 * Clicking optimistically toggles the cell and POSTs the updated permission set
 * for that role. On failure the toggle reverts and a toast is shown.
 */
const PermissionMatrix = () => {
  const { token } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  // map: role_id -> Set of permission_id
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingCell, setSavingCell] = useState(null); // `${roleId}:${permissionId}`
  const [query, setQuery] = useState('');

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${API}/rbac/roles`, { headers }),
        axios.get(`${API}/rbac/permissions`, { headers }),
      ]);
      const rolesData = (rolesRes.data?.roles || rolesRes.data || []).filter(
        (r) => r.is_active !== false
      );
      const permsData = permsRes.data?.permissions || permsRes.data || [];

      // Fetch role↔permission assignments in parallel
      const grants = await Promise.all(
        rolesData.map((r) =>
          axios
            .get(`${API}/rbac/roles/${r.id}/permissions`, { headers })
            .then((res) => ({
              roleId: r.id,
              permIds: new Set((res.data?.permissions || []).map((p) => p.id)),
            }))
            .catch(() => ({ roleId: r.id, permIds: new Set() }))
        )
      );
      const next = {};
      grants.forEach((g) => {
        next[g.roleId] = g.permIds;
      });

      setRoles(rolesData);
      setPermissions(permsData);
      setAssignments(next);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail[0]?.msg || 'Failed to load permission matrix'
          : detail?.msg || e.message || 'Failed to load permission matrix';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (token) loadMatrix();
  }, [token, loadMatrix]);

  const toggleCell = async (role, permission) => {
    const cellKey = `${role.id}:${permission.id}`;
    if (savingCell) return;

    const current = assignments[role.id] || new Set();
    const willAdd = !current.has(permission.id);

    // Optimistic update
    const optimistic = new Set(current);
    if (willAdd) optimistic.add(permission.id);
    else optimistic.delete(permission.id);
    setAssignments((prev) => ({ ...prev, [role.id]: optimistic }));
    setSavingCell(cellKey);

    try {
      await axios.post(
        `${API}/rbac/roles/${role.id}/permissions`,
        { permission_ids: Array.from(optimistic) },
        { headers }
      );
      toast.success(
        `${willAdd ? 'Granted' : 'Revoked'} ${permission.name} ${
          willAdd ? 'to' : 'from'
        } ${role.name}`
      );
    } catch (e) {
      // Revert
      setAssignments((prev) => ({ ...prev, [role.id]: current }));
      const detail = e.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail[0]?.msg || 'Failed to update permission'
          : detail?.msg || e.message || 'Failed to update permission';
      toast.error(msg);
    } finally {
      setSavingCell(null);
    }
  };

  // Group permissions by module (alphabetical), then filter by search query
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return permissions.filter((p) => {
      if (!q) return true;
      return (
        (p.name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.module || '').toLowerCase().includes(q)
      );
    });
  }, [permissions, query]);

  const groupedByModule = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      const mod = p.module || 'other';
      if (!map[mod]) map[mod] = [];
      map[mod].push(p);
    });
    Object.keys(map).forEach((m) =>
      map[m].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    );
    return map;
  }, [filtered]);

  const sortedModules = useMemo(() => Object.keys(groupedByModule).sort(), [groupedByModule]);

  const isAdminRole = (role) => role?.name === 'admin';

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Shield size={28} className="text-cyan-600" />
            Permission Matrix
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Toggle any cell to grant or revoke a permission. Changes save instantly.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadMatrix}
          disabled={loading}
          data-testid="reload-matrix"
        >
          <RotateCcw size={14} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4">
        <div className="relative max-w-md">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            placeholder="Search by permission name, description, or module..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="matrix-search"
          />
        </div>
      </div>

      {/* Matrix */}
      {loading ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 flex items-center justify-center text-slate-500">
          <Loader2 className="animate-spin mr-2" size={18} />
          Loading roles and permissions...
        </div>
      ) : roles.length === 0 || permissions.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-500">
          <AlertTriangle className="mx-auto mb-2" size={24} />
          No roles or permissions found. Seed RBAC from the RBAC Debug page first.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700 border-b border-slate-200 sticky left-0 bg-slate-50 z-20 min-w-[280px]">
                  Permission
                </th>
                {roles.map((r) => (
                  <th
                    key={r.id}
                    className="text-center px-3 py-3 font-semibold text-slate-700 border-b border-l border-slate-200 whitespace-nowrap min-w-[120px]"
                    data-testid={`matrix-role-header-${r.name}`}
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <span>{r.name}</span>
                      <span className="text-[10px] font-normal text-slate-400">
                        {(assignments[r.id] || new Set()).size} perms
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedModules.map((mod) => (
                <React.Fragment key={mod}>
                  <tr className="bg-slate-50/60">
                    <td
                      colSpan={1 + roles.length}
                      className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 sticky left-0 z-10"
                    >
                      {mod}
                    </td>
                  </tr>
                  {groupedByModule[mod].map((p) => (
                    <tr
                      key={p.id}
                      className="hover:bg-cyan-50/30"
                      data-testid={`matrix-row-${p.name}`}
                    >
                      <td className="px-4 py-2 border-b border-slate-100 sticky left-0 bg-white z-10">
                        <div className="font-medium text-slate-800">{p.name}</div>
                        {p.description && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {p.description}
                          </div>
                        )}
                      </td>
                      {roles.map((r) => {
                        const cellKey = `${r.id}:${p.id}`;
                        const granted = assignments[r.id]?.has(p.id);
                        const isAdmin = isAdminRole(r);
                        const isSaving = savingCell === cellKey;
                        return (
                          <td
                            key={r.id}
                            className="px-3 py-2 border-b border-l border-slate-100 text-center"
                          >
                            <button
                              type="button"
                              onClick={() => !isAdmin && !isSaving && toggleCell(r, p)}
                              disabled={isAdmin || isSaving}
                              data-testid={`matrix-cell-${r.name}-${p.name}`}
                              title={
                                isAdmin
                                  ? 'Admin role has implicit access to everything'
                                  : granted
                                  ? 'Click to revoke'
                                  : 'Click to grant'
                              }
                              className={`w-6 h-6 rounded inline-flex items-center justify-center transition-all ${
                                isAdmin
                                  ? 'bg-cyan-100 border border-cyan-200 text-cyan-700 cursor-not-allowed'
                                  : granted
                                  ? 'bg-green-500 border border-green-600 text-white hover:opacity-80'
                                  : 'border border-slate-300 bg-white hover:border-cyan-400 hover:bg-cyan-50'
                              }`}
                            >
                              {isSaving ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : isAdmin ? (
                                <span className="text-[10px] font-bold">∗</span>
                              ) : granted ? (
                                <span className="text-[12px] font-bold">✓</span>
                              ) : null}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {sortedModules.length === 0 && (
                <tr>
                  <td
                    colSpan={1 + roles.length}
                    className="px-4 py-12 text-center text-slate-500"
                  >
                    No permissions match "{query}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded bg-green-500 inline-flex items-center justify-center text-white font-bold text-[10px]">✓</span>
          Granted
        </div>
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded border border-slate-300 bg-white"></span>
          Not granted
        </div>
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded bg-cyan-100 border border-cyan-200 inline-flex items-center justify-center text-cyan-700 font-bold text-[10px]">∗</span>
          Admin (implicit all)
        </div>
      </div>
    </div>
  );
};

export default PermissionMatrix;
